import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subDays,
  format,
} from 'date-fns';

export type QuickFilterPreset =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'last7Days'
  | 'last30Days'
  | 'thisQuarter'
  | 'thisYear';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface DateRangeQuickFilterProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onRangeChange?: (range: DateRange) => void;
  showPresets?: QuickFilterPreset[];
  className?: string;
}

const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd');

const getPresetDates = (preset: QuickFilterPreset): DateRange => {
  const now = new Date();

  switch (preset) {
    case 'today':
      return {
        startDate: formatDate(now),
        endDate: formatDate(now),
      };
    case 'yesterday':
      const yesterday = subDays(now, 1);
      return {
        startDate: formatDate(yesterday),
        endDate: formatDate(yesterday),
      };
    case 'thisWeek':
      return {
        startDate: formatDate(startOfWeek(now, { weekStartsOn: 1 })),
        endDate: formatDate(endOfWeek(now, { weekStartsOn: 1 })),
      };
    case 'lastWeek':
      const lastWeekStart = startOfWeek(subDays(now, 7), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subDays(now, 7), { weekStartsOn: 1 });
      return {
        startDate: formatDate(lastWeekStart),
        endDate: formatDate(lastWeekEnd),
      };
    case 'thisMonth':
      return {
        startDate: formatDate(startOfMonth(now)),
        endDate: formatDate(endOfMonth(now)),
      };
    case 'lastMonth':
      const lastMonth = subDays(now, now.getDate());
      return {
        startDate: formatDate(startOfMonth(lastMonth)),
        endDate: formatDate(endOfMonth(lastMonth)),
      };
    case 'last7Days':
      return {
        startDate: formatDate(subDays(now, 6)),
        endDate: formatDate(now),
      };
    case 'last30Days':
      return {
        startDate: formatDate(subDays(now, 29)),
        endDate: formatDate(now),
      };
    case 'thisQuarter':
      return {
        startDate: formatDate(startOfQuarter(now)),
        endDate: formatDate(endOfQuarter(now)),
      };
    case 'thisYear':
      return {
        startDate: formatDate(startOfYear(now)),
        endDate: formatDate(endOfYear(now)),
      };
    default:
      return { startDate: '', endDate: '' };
  }
};

const DEFAULT_PRESETS: QuickFilterPreset[] = [
  'today',
  'thisWeek',
  'thisMonth',
  'last7Days',
  'last30Days',
  'thisQuarter',
];

export const DateRangeQuickFilter: React.FC<DateRangeQuickFilterProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRangeChange,
  showPresets = DEFAULT_PRESETS,
  className = '',
}) => {
  const { t } = useTranslation();
  const [selectedPreset, setSelectedPreset] = React.useState('');

  // Check if current dates match any preset
  React.useEffect(() => {
    if (!startDate || !endDate) return;
    
    for (const preset of showPresets) {
      const range = getPresetDates(preset);
      if (range.startDate === startDate && range.endDate === endDate) {
        setSelectedPreset(preset);
        return;
      }
    }
    setSelectedPreset(''); // Custom range
  }, [startDate, endDate, showPresets]);

  const handlePresetChange = (preset: string) => {
    if (!preset) {
      setSelectedPreset('');
      return;
    }
    const range = getPresetDates(preset as QuickFilterPreset);
    setSelectedPreset(preset);
    onStartDateChange(range.startDate);
    onEndDateChange(range.endDate);
    onRangeChange?.(range);
  };

  const handleManualDateChange = (date: string, isStartDate: boolean) => {
    if (isStartDate) {
      onStartDateChange(date);
    } else {
      onEndDateChange(date);
    }
  };

  return (
    <div className={`flex items-end space-x-4 ${className}`}>
      {/* Quick filter dropdown */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t('dateFilter.quickFilter')}
        </label>
        <select
          value={selectedPreset}
          onChange={(e) => handlePresetChange(e.target.value)}
          className="input-field"
        >
          <option value="">{t('dateFilter.customRange')}</option>
          {showPresets.map((preset) => (
            <option key={preset} value={preset}>
              {t(`dateFilter.${preset}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Date inputs */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t('reports.fromDate')}
        </label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => handleManualDateChange(e.target.value, true)}
          className="input-field"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">
          {t('reports.toDate')}
        </label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => handleManualDateChange(e.target.value, false)}
          className="input-field"
        />
      </div>
    </div>
  );
};

export default DateRangeQuickFilter;
